/** Affiche une date française ; une date sans heure reste un jour local, sans décalage UTC. */
export function formaterDateCarte(valeur?: string | null, avecJour = false): string | null {
  const date = lireDateCarte(valeur);
  if (!date) return null;
  return new Intl.DateTimeFormat('fr-FR', {
    ...(avecJour ? { weekday: 'short' as const } : {}),
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/**
 * Affiche uniquement la plus grande unité entière : années, mois, jours, heures, minutes.
 * Les mois/années sont calendaires (pas des blocs de 30/365 jours). Le calcul reçoit
 * l'horloge du composant pour rester pur et être actualisé chaque minute.
 */
export function formaterAnciennete(
  valeur: string | null | undefined,
  maintenant: number,
): string | null {
  const date = lireDateCarte(valeur);
  if (!date) return null;
  const mois = compterMoisComplets(date, new Date(maintenant));
  const annees = Math.floor(mois / 12);
  if (annees > 0) return `il y a ${annees} an${annees > 1 ? 's' : ''}`;
  if (mois > 0) return `il y a ${mois} mois`;
  const minutes = Math.max(0, Math.floor((maintenant - date.getTime()) / 60_000));
  const jours = Math.floor(minutes / 1440);
  const heures = Math.floor(minutes / 60);
  if (jours > 0) return `il y a ${jours} jour${jours > 1 ? 's' : ''}`;
  if (heures > 0) return `il y a ${heures} heure${heures > 1 ? 's' : ''}`;
  if (minutes === 0) return 'il y a moins d’une minute';
  return `il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
}

/** Un mois est complet à l'anniversaire local, ramené au dernier jour si nécessaire. */
function compterMoisComplets(debut: Date, fin: Date): number {
  const mois = (fin.getFullYear() - debut.getFullYear()) * 12 + fin.getMonth() - debut.getMonth();
  const dernierJour = new Date(fin.getFullYear(), fin.getMonth() + 1, 0).getDate();
  const anniversaire = new Date(debut);
  // Ramène les 29, 30 et 31 au dernier jour des mois plus courts.
  anniversaire.setDate(1);
  anniversaire.setFullYear(
    fin.getFullYear(),
    fin.getMonth(),
    Math.min(debut.getDate(), dernierJour),
  );
  return Math.max(0, mois - (anniversaire > fin ? 1 : 0));
}

/** Une échéance affichée au jour reste à venir jusqu'à la fin de ce jour. */
export function dateInterventionDepassee(
  valeur: string | null | undefined,
  maintenant: number,
): boolean {
  const date = lireDateCarte(valeur);
  if (!date) return false;
  const aujourdHui = new Date(maintenant);
  date.setHours(0, 0, 0, 0);
  aujourdHui.setHours(0, 0, 0, 0);
  return date.getTime() < aujourdHui.getTime();
}

/** Les jours YYYY-MM-DD n'ont pas de fuseau ; les horodatages gardent celui fourni par l'API. */
function lireDateCarte(valeur?: string | null): Date | null {
  if (!valeur) return null;
  const sansHeure = /^\d{4}-\d{2}-\d{2}$/.test(valeur);
  const date = new Date(sansHeure ? `${valeur}T00:00:00` : valeur);
  if (!Number.isFinite(date.getTime())) return null;
  if (sansHeure) {
    const [annee, mois, jour] = valeur.split('-').map(Number);
    if (date.getFullYear() !== annee || date.getMonth() + 1 !== mois || date.getDate() !== jour) {
      return null;
    }
  }
  return date;
}
